import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { supabase } from "@/lib/supabase"
import { useAuthStore } from "@/stores/authStore"
import { useOrgStore } from "@/stores/orgStore"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type InviteRole = "admin" | "editor" | "viewer"

interface InviteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onInviteCreated: () => void
}

export function InviteModal({ open, onOpenChange, onInviteCreated }: InviteModalProps) {
  const { user } = useAuthStore()
  const { currentOrg } = useOrgStore()
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<InviteRole>("viewer")
  const [loading, setLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)

  const resetForm = () => {
    setEmail("")
    setRole("viewer")
    setEmailError(null)
    setInviteUrl(null)
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return "Email is required"
    if (!EMAIL_REGEX.test(value)) return "Please enter a valid email address"
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const error = validateEmail(email)
    if (error) {
      setEmailError(error)
      return
    }

    if (!currentOrg?.id || !user?.id) {
      toast.error("Organization not found")
      return
    }

    setLoading(true)
    setEmailError(null)

    // Check if there's already a pending invite for this email
    const { data: existingInvite } = await supabase
      .from("invites")
      .select("id")
      .eq("org_id", currentOrg.id)
      .eq("email", email.toLowerCase())
      .is("accepted_at", null)
      .gt("expires_at", new Date().toISOString())
      .maybeSingle()

    if (existingInvite) {
      setEmailError("An invite has already been sent to this email")
      setLoading(false)
      return
    }

    // Create the invite
    const { data: invite, error: inviteError } = await supabase
      .from("invites")
      .insert({
        org_id: currentOrg.id,
        email: email.toLowerCase(),
        role,
        invited_by: user.id,
      })
      .select("token")
      .single()

    if (inviteError) {
      toast.error("Failed to create invite")
      console.error(inviteError)
      setLoading(false)
      return
    }

    const url = `${window.location.origin}/accept-invite?token=${invite.token}`
    setInviteUrl(url)

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(url)
      toast.success(`Invite link copied! Share with ${email}`)
    } catch {
      toast.success("Invite created! Copy the link below.")
    }

    onInviteCreated()
    setLoading(false)
  }

  const handleCopyLink = async () => {
    if (!inviteUrl) return
    try {
      await navigator.clipboard.writeText(inviteUrl)
      toast.success("Link copied to clipboard")
    } catch {
      toast.error("Failed to copy link")
    }
  }

  const handleDone = () => {
    handleClose()
  }

  // Show success state with invite link
  if (inviteUrl) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Sent!</DialogTitle>
            <DialogDescription>
              Share this link with {email} to invite them to {currentOrg?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={inviteUrl}
                className="font-mono text-xs"
              />
              <Button type="button" variant="outline" onClick={handleCopyLink}>
                Copy
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              This invite link expires in 7 days.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={handleDone}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Invite someone to join {currentOrg?.name || "your organization"}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailError(null)
                }}
                disabled={loading}
                className={emailError ? "border-destructive" : ""}
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={role}
                onValueChange={(value: InviteRole) => setRole(value)}
                disabled={loading}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex flex-col">
                      <span className="font-medium">Admin</span>
                      <span className="text-xs text-muted-foreground">
                        Full access, can invite and manage team
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex flex-col">
                      <span className="font-medium">Editor</span>
                      <span className="text-xs text-muted-foreground">
                        Can edit content and manage contacts
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex flex-col">
                      <span className="font-medium">Viewer</span>
                      <span className="text-xs text-muted-foreground">
                        Read-only access to dashboard
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
